import { Container, FederatedPointerEvent, IDestroyOptions, Point, Sprite, Text, TextStyle } from "pixi.js";
import { ButtonWithScaleAnimation } from "../../../common/components/ButtonWithScaleAnimation";
import { GameFont } from "../../../common/GameFont";
import { Avatar } from "../../../common/components/Avatar";
import { IPlayerModel } from "../../../common/models/IPlayerModel";
import { nameof } from "../../../../../shared/bindable-data";
import { IGuestModel } from "../../../common/models/IGuestModel";
import { UserProfileResourcePackage } from "../../../popups/userprofile/resources/UserProfileResourcePackage";
import { Event } from "../../../../../shared/event-system/Event";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";

export class AvatarField extends Container {
    private readonly commonResources: CommonResourcesPackage;
    private readonly userProfileResources: UserProfileResourcePackage;
    private readonly playerModel: IPlayerModel;
    private readonly guestModel: IGuestModel;

    private background: Sprite;
    private profileAvatar: Avatar;
    private userLevel: Text;

    public get onProfileButtonClick(): Event<ButtonWithScaleAnimation, FederatedPointerEvent> {
        return this.profileAvatar.onProfileButtonClick;
    }

    public constructor(
        commonResources: CommonResourcesPackage,
        userProfileResources: UserProfileResourcePackage,
        playerModel: IPlayerModel,
        guestModel: IGuestModel
    ) {
        super();
        this.commonResources = commonResources;
        this.userProfileResources = userProfileResources;
        this.playerModel = playerModel;
        this.guestModel = guestModel;
        this.build();
        this.layout();
        this.playerModel.propertyChanged.add(this.onPlayerModelPropertyChanged, this);
        this.guestModel.propertyChanged.add(this.onGuestModelPropertyChanged, this);
        this.once("added", this.onAddedToStage, this);
    }

    private async onPlayerModelPropertyChanged(model: IPlayerModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "authorizedVia"):
                this.profileAvatar.glow = model.isAuthorized;
                break;
            case nameof(model, "lastLeaderboardPlace"):
                this.profileAvatar.updateLeaderboardIcon(model.lastLeaderboardPlace.value);
                break;
            case nameof(model, "level"):
                this.updateUserLevel(model.level.value);
                break;
        }
    }

    private async onGuestModelPropertyChanged(model: IGuestModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "avatarTexture"):
                this.updateProfileIcon();
                break;
        }
    }


    private onAddedToStage(): void {
        this.profileAvatar.glow = this.playerModel.isAuthorized;
        this.updateProfileIcon();
    }


    private build(): void {
        this.background = new Sprite(this.commonResources.assets_common_ui_top_panel_avatar_field_png);
        this.addChild(this.background);
        this.profileAvatar = new Avatar(this.userProfileResources, this.guestModel.avatarTexture.value);
        this.profileAvatar.avatarSize = new Point(80, 80);
        this.profileAvatar.trophySize = new Point(31, 40);
        this.profileAvatar.updateLeaderboardIcon(this.playerModel.lastLeaderboardPlace.value);
        this.addChild(this.profileAvatar);
        this.userLevel = new Text(`LV. ${this.playerModel.level.value}`, new userLevelTextStyle());
        this.addChild(this.userLevel);
    }

    private updateProfileIcon(): void {
        this.profileAvatar.updateTexture(this.guestModel.avatarTexture.value);
    }

    private updateUserLevel(value: number): void {
        this.userLevel.text = `LV. ${value}`, new userLevelTextStyle();
        this.userLevel.position.set(this.profileAvatar.x + this.profileAvatar.width + 10, (this.background.height - this.userLevel.height) * 0.5);
    }

    private layout(): void {
        this.profileAvatar.position.y = 0;
        this.userLevel.position.set(this.profileAvatar.x + this.profileAvatar.width + 10, (this.background.height - this.userLevel.height) * 0.5);
    }

    public destroy(options?: boolean | IDestroyOptions): void {
        this.playerModel.propertyChanged.remove(this.onPlayerModelPropertyChanged, this);
        this.guestModel.propertyChanged.remove(this.onGuestModelPropertyChanged, this);
        super.destroy(options);
    }
}

class userLevelTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 36
        }));
    }
}
